import React, { Suspense, useEffect } from 'react'
import { connect } from 'react-redux'
import { AppState } from './reducers'
import { UI } from './constants/ui'
import { AdminUI } from './constants/admin/adminUi'
import Home from './components/Views/Home'
import ConfirmLicencePlate from './components/Views/ConfirmLicencePlate'
import LicencePlateAuth from './components/Views/LicencePlateAuth'
import ConfirmReservation from './components/Views/ConfirmReservation'
import Trailer from './components/Views/Trailer'
import Cargo from './components/Views/Cargo'
import CargoContent from './components/Views/CargoContent'
import CargoWeight from './components/Views/CargoWeight'
import Consignee from './components/Views/Consignee'
import FromTo from './components/Views/FromTo'
import ConfirmConnection from './components/Views/ConfirmConnection'
import DriversInformation from './components/Views/DriversInformation'
import DriversDocument from './components/Views/DriversDocument'
import Driver2Information from './components/Views/Driver2Information'
import Driver2Document from './components/Views/Driver2Document'
import MoreThanOnePassenger from './components/Views/MoreThanOnePassenger'
import ConfirmInformation from './components/Views/ConfirmInformation'
import CheckinDone from './components/Views/CheckinDone'
import Summary from './components/Views/Summary'
import Error from './components/Views/Error'
import OutOfService from './components/Views/OutOfService'
import BlockUI from './components/BlockUI'
import TimeOutMessage from './components/TimeOutMessage'
import AlreadyCheckedIn from './components/Views/AlreadyCheckedIn'
import PrintSWB from './components/Views/PrintSWB'
import './App.less'
import AdminLogin from './components/Admin/Login'
import AdminMain from './components/Admin/Main'
import Terminal from './components/Admin/Terminal'
import DangerousGoods from './components/Views/DangerousGoods'
import LanguageSelection from './components/Views/LanguageSelection'
import Welcome from './components/Views/Welcome'
import DriversInformationAndDocumentMobile from './components/Views/DriversInformationAndDocumentMobile'
import Driver2InformationAndDocumentMobile from './components/Views/Driver2InformationAndDocumentMobile'
import { getMode } from './hooks/mode'
import { MODE } from './constants/mode'
import DownloadSWB from './components/Views/DownloadSWB'
import { getStateFromLocalStorageAction } from './actions/ui'
import SelfCheckService from './components/SelfCheckService'

const mapStateToProps = (state: AppState) => ({
  uiState: state.ui.uiState,
  isLoading: state.reservation.isLoading,
  isTimeOutMessage: state.ui.isTimeOutMessage,
  AdminUiState: state.adminUi.adminUiState,
  isLoginLoading: state.adminTerminals.isLoginLoading,
  isDepartureLoading: state.adminTerminals.isDepartureLoading,
  isUiConfigLoading: state.adminTerminals.isUiConfigLoading,
  isGetAllTerminalsLoading: state.adminTerminals.isGetAllTerminalsLoading,
  isTerminalSaving: state.adminTerminals.isTerminalSaving,
})
type StateProps = ReturnType<typeof mapStateToProps>

const mapDispatchToProps = {
  getStateFromLocalStorage: getStateFromLocalStorageAction,
}
type DispatchProps = typeof mapDispatchToProps

type Props = StateProps & DispatchProps

const router = (uiState: UI) => {
  switch (uiState) {
    case UI.WELCOME:
      return <Welcome />
    case UI.MAIN:
      return <Home />
    case UI.CONFIRM_LICENCE_PLATE:
      return <ConfirmLicencePlate />
    case UI.LICENCE_PLATE_AUTH:
      return <LicencePlateAuth />
    case UI.CONFIRM_RESERVATION:
      return <ConfirmReservation />
    case UI.TRAILER:
      return <Trailer />
    case UI.CARGO_LENGTH:
      return <Cargo />
    case UI.CARGO_CONTENT:
      return <CargoContent />
    case UI.CONSIGNEE:
      return <Consignee />
    case UI.CARGO_WEIGHT:
      return <CargoWeight />
    case UI.FROM_TO:
      return <FromTo />
    case UI.CONFIRM_CONNECTION:
      return <ConfirmConnection />
    case UI.DRIVERS_INFORMATION:
      return <DriversInformation />
    case UI.DRIVERS_INFORMATION_AND_DOCUMENT_MOBILE:
      return <DriversInformationAndDocumentMobile />
    case UI.DOCUMENT_INFORMATION:
      return <DriversDocument />
    case UI.DRIVER2_INFORMATION:
      return <Driver2Information />
    case UI.DRIVER2_INFORMATION_AND_DOCUMENT_MOBILE:
      return <Driver2InformationAndDocumentMobile />
    case UI.DOCUMENT2_INFORMATION:
      return <Driver2Document />
    case UI.MORE_THAN_ONE_PASSENGER:
      return <MoreThanOnePassenger />
    case UI.SUMMARY:
      return <Summary />
    case UI.CONFIRM_RESERVATION_INFO:
      return <ConfirmInformation />
    case UI.CHECKIN_DONE:
      return <CheckinDone />
    case UI.ERROR:
      return <Error />
    case UI.OUT_OF_SERVICE:
      return <OutOfService />
    case UI.ALREADY_CHECKED_IN:
      return <AlreadyCheckedIn />
    case UI.PRINT_SWB:
      return <PrintSWB />
    case UI.DANGEROUS_GOODS:
      return <DangerousGoods />
    case UI.DOWNLOAD_SWB:
      return <DownloadSWB />
    default:
      return <LanguageSelection />
  }
}

const Loader = () => (
  <div className="App">
    <div>loading...</div>
  </div>
)
const routerAdmin = (uiStateAdmin: AdminUI) => {
  switch (uiStateAdmin) {
    case AdminUI.ALL_TERMINALS:
      return <AdminMain />
    case AdminUI.TERMINAL:
      return <Terminal />
    default:
      return <AdminLogin />
  }
}

const App: React.FC<Props> = ({
  uiState,
  isLoading,
  isTimeOutMessage,
  AdminUiState,
  isLoginLoading,
  isDepartureLoading,
  isUiConfigLoading,
  isGetAllTerminalsLoading,
  isTerminalSaving,
  getStateFromLocalStorage,
}: Props) => {
  const View = router(uiState)
  const ViewAdmin = routerAdmin(AdminUiState)
  const location = window.location.pathname
  const AdminIsLoading =
    isLoginLoading || isDepartureLoading || isUiConfigLoading || isGetAllTerminalsLoading || isTerminalSaving

  const mode = getMode()
  const showTimeOutMessage = location !== '/admin' && mode === MODE.KIOSK && !isLoading && isTimeOutMessage
  const startSelfCheck = mode === MODE.KIOSK

  const setInnerHeight = () => {
    document.documentElement.style.setProperty('--innerHeight', `${window.innerHeight}px`);
  }

  useEffect(() => {
    if (getMode() !== MODE.KIOSK) {
      window.scrollTo(0, 0)
    }
  }, [View])
  
  useEffect(() => {
    if (getMode() !== MODE.KIOSK) {
      getStateFromLocalStorage()
      window.addEventListener('resize', setInnerHeight);
      setInnerHeight()
    }
    return () => window.removeEventListener('resize', setInnerHeight)
  }, [])

  return (
    <Suspense fallback={<Loader />}>
      {location === '/admin' && <div className="App">{ViewAdmin}</div>}
      {location !== '/admin' && <div className="App">{View}</div>}
      {showTimeOutMessage && <TimeOutMessage />}
      {(isLoading || AdminIsLoading) && <BlockUI />}
      {startSelfCheck && <SelfCheckService />}
    </Suspense>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(App)
